import { useSWRBasketSessionState } from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { type CheckoutStep } from "@/app/(site)/contexts/ecommerce/types"

export interface StepValidationResult {
    isValid: boolean
    errorStep?: CheckoutStep
    errorMessage?: string
}

export interface StepValidationData {
    hasEmail?: boolean
    hasFirstName?: boolean
    hasLastName?: boolean
    hasAddress?: boolean
    hasCity?: boolean
    hasZipCode?: boolean
    hasCountry?: boolean
    hasPhoneNumber?: boolean
    hasShippingMethod?: boolean
    hasPaymentMethod?: boolean
}

export const validateStepData = (data: StepValidationData): StepValidationResult => {
    // Validate Step 1 - Customer Information
    const step1RequiredFields = [
        "hasEmail",
        "hasFirstName",
        "hasLastName",
        "hasAddress",
        "hasCity",
        "hasZipCode",
        "hasCountry",
        "hasPhoneNumber"
    ]

    const step1MissingFields = step1RequiredFields.filter(
        field => !data[field as keyof StepValidationData]
    )

    if (step1MissingFields.length > 0) {
        return {
            isValid: false,
            errorStep: 1,
            errorMessage: `Missing required fields: ${step1MissingFields.join(", ")}`
        }
    }

    // Validate Step 2 - Shipping Method
    if (!data.hasShippingMethod) {
        return {
            isValid: false,
            errorStep: 2,
            errorMessage: "No shipping method selected"
        }
    }

    // All validations passed
    return {
        isValid: true
    }
}

export const extractStepValidationData = (
    selection: any,
    shippingMethods: any[] = []
): StepValidationData => {
    const address = selection?.address || {}
    const shippingAddress = selection?.shippingAddress || {}
    const shippingMethod = selection?.shippingMethod

    // Check if the selected shipping method is valid (exists in available methods)
    // shippingMethod can be a string ID or an array
    const isValidShippingMethod =
        !!shippingMethod &&
        shippingMethods.length > 0 &&
        (Array.isArray(shippingMethod)
            ? shippingMethod.length > 0 &&
              shippingMethods.some(
                  method =>
                      method.shippingMethod === shippingMethod[0] ||
                      method.id === shippingMethod[0]
              )
            : shippingMethods.some(
                  method =>
                      method.shippingMethod === shippingMethod ||
                      method.id === shippingMethod
              ))

    return {
        // Step 1 validation
        hasEmail: !!(address.email || shippingAddress.email),
        hasFirstName: !!(address.firstName || shippingAddress.firstName),
        hasLastName: !!(address.lastName || shippingAddress.lastName),
        hasAddress: !!(address.address1 || shippingAddress.address1),
        hasCity: !!(address.city || shippingAddress.city),
        hasZipCode: !!(address.zipCode || shippingAddress.zipCode),
        hasCountry: !!(address.country || shippingAddress.country),
        hasPhoneNumber: !!(address.phoneNumber || shippingAddress.phoneNumber),

        // Step 2 validation
        hasShippingMethod: !!isValidShippingMethod,

        // Step 3 validation (placeholder for future)
        hasPaymentMethod: false // Will be implemented when payment step is ready
    }
}

export const useStepValidation = () => {
    const { selection, isLoadingSelection, shippingMethods } = useSWRBasketSessionState()

    const validateCurrentStep = (): StepValidationResult => {
        if (isLoadingSelection || !selection) {
            return {
                isValid: false,
                errorStep: 1,
                errorMessage: "Loading selection data..."
            }
        }

        const validationData = extractStepValidationData(selection, shippingMethods)

        const result = validateStepData(validationData)

        return result
    }

    const validateSteps = (
        steps: CheckoutStep[]
    ): { isValid: boolean; firstErrorStep?: CheckoutStep } => {
        if (isLoadingSelection || !selection) {
            return {
                isValid: false,
                firstErrorStep: 1
            }
        }

        const validationData = extractStepValidationData(selection, shippingMethods)

        for (const step of steps) {
            if (step === 1) {
                const step1RequiredFields = [
                    "hasEmail",
                    "hasFirstName",
                    "hasLastName",
                    "hasAddress",
                    "hasCity",
                    "hasZipCode",
                    "hasCountry",
                    "hasPhoneNumber"
                ]

                const step1MissingFields = step1RequiredFields.filter(
                    field => !validationData[field as keyof StepValidationData]
                )

                if (step1MissingFields.length > 0) {
                    return { isValid: false, firstErrorStep: 1 }
                }
            }

            if (step === 2) {
                if (!validationData.hasShippingMethod) {
                    return { isValid: false, firstErrorStep: 2 }
                }
            }

            if (step === 3) {
                if (!validationData.hasPaymentMethod) {
                    return { isValid: false, firstErrorStep: 3 }
                }
            }
        }

        return { isValid: true }
    }

    const getValidStep = (targetStep: CheckoutStep): CheckoutStep => {
        const validation = validateCurrentStep()
        if (validation.isValid) {
            return targetStep
        }

        // Return the step with errors, or step 1 as fallback
        return validation.errorStep || 1
    }

    return {
        validateCurrentStep,
        validateSteps,
        getValidStep,
        isLoadingValidation: isLoadingSelection
    }
}
